// src/Components/DocumentScanner/DocumentScannerDialog.js
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close as CloseIcon, Scanner as ScannerIcon } from '@mui/icons-material';
import DocumentScanner from './DocumentScanner';

/**
 * DocumentScannerDialog - A dialog wrapper for the DocumentScanner component
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {function} props.onClose - Callback when dialog is closed
 * @param {function} props.onComplete - Callback when scan is complete with { fileName, downloadURL, pageCount }
 * @param {string} props.destinationPath - Path to upload the document to (default: '/')
 * @param {string} props.destinationType - Type of destination: 'personal' | 'family' | 'vault'
 * @param {string} props.storageStrategy - 'fileExplorer' | 'documentService'
 */
const DocumentScannerDialog = ({
  open,
  onClose,
  onComplete,
  destinationPath = '/',
  destinationType = 'personal',
  storageStrategy = 'fileExplorer',
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const handleComplete = (result) => {
    if (onComplete) {
      onComplete(result);
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          minHeight: fullScreen ? '100vh' : 600,
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ScannerIcon color="primary" />
          <Typography variant="h6" component="span">
            Scan Document
          </Typography>
        </Box>
        <IconButton onClick={onClose} edge="end" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <DocumentScanner
          onComplete={handleComplete}
          onCancel={onClose}
          destinationPath={destinationPath}
          destinationType={destinationType}
          storageStrategy={storageStrategy}
        />
      </DialogContent>
    </Dialog>
  );
};

export default DocumentScannerDialog;
